import { create } from 'zustand'
import type { Production, AdminUser } from '@/types/database'

interface AppState {
  // Featured production
  featuredProduction: Production | null
  setFeaturedProduction: (production: Production | null) => void

  // UI State
  isCheckoutLoading: boolean
  setIsCheckoutLoading: (loading: boolean) => void

  // Session ID for analytics
  sessionId: string | null
  setSessionId: (id: string) => void
  getOrCreateSessionId: () => string

  // Admin state
  adminUser: AdminUser | null
  setAdminUser: (user: AdminUser | null) => void
}

// Generate a random session ID
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
}

export const useStore = create<AppState>((set, get) => ({
  // Featured production
  featuredProduction: null,
  setFeaturedProduction: (production) => set({ featuredProduction: production }),

  // UI State
  isCheckoutLoading: false,
  setIsCheckoutLoading: (loading) => set({ isCheckoutLoading: loading }),

  // Session ID
  sessionId: null,
  setSessionId: (id) => set({ sessionId: id }),
  getOrCreateSessionId: () => {
    const current = get().sessionId
    if (current) return current

    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('ion_session_id')
      if (stored) {
        set({ sessionId: stored })
        return stored
      }
    }

    // Generate new session ID
    const newId = generateSessionId()
    if (typeof window !== 'undefined') {
      localStorage.setItem('ion_session_id', newId)
    }
    set({ sessionId: newId })
    return newId
  },

  // Admin state
  adminUser: null,
  setAdminUser: (user) => set({ adminUser: user }),
}))

// Selector hooks for convenience
export const useAdminUser = () => useStore((state) => state.adminUser)
